import { useRef, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Upload, X } from "lucide-react";

const MAX_BYTES = 2 * 1024 * 1024;
const SHOWN = 200;
const ACTION = {
  create: { label: "New", pill: "bg-green-100 text-green-900" },
  update: { label: "Update", pill: "bg-blue-100 text-blue-900" },
  skip: { label: "Skipped", pill: "bg-neutral-100 text-neutral-700" },
  error: { label: "Problem", pill: "bg-red-100 text-red-900" },
};
const button = "px-4 text-xs font-semibold uppercase tracking-wider min-h-[44px] inline-flex items-center justify-center gap-2 disabled:opacity-50";

/**
 * Bulk upload of services (Tenant Admin → Services): a spreadsheet with one
 * service per row, CSV or Excel.
 *
 * Choosing a file only asks the server what the upload WOULD do; nothing is
 * changed until "Import" is pressed. The server reads and checks the file
 * both times (backend/bulk_services.py): this screen shows its answer.
 *
 *   onClose()     the panel is closed
 *   onImported()  services were added or changed: the list should be reloaded
 */
export default function BulkServiceUpload({ onClose, onImported }) {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [plan, setPlan] = useState(null);         // the server's answer: a preview (dry_run) or the result
  const [busy, setBusy] = useState("");           // "checking" | "importing" | "template" | "export"
  const [onlyProblems, setOnlyProblems] = useState(false);
  // Set the moment Import is pressed. A second press in the same instant (a
  // double tap) would otherwise send the file twice: the server adds nothing
  // the second time, but its "0 added" answer would replace the real result.
  const importing = useRef(false);

  const explain = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    toast.error(typeof detail === "string" ? detail : (err?.response ? fallback : "No connection, or the file changed after it was chosen. Choose the file again."));
  };

  const send = async (chosen, dryRun, update) => {
    const body = new FormData();
    body.append("file", chosen);
    body.append("dry_run", dryRun ? "true" : "false");
    body.append("update_existing", update ? "true" : "false");
    return (await api.post("/tenant/services/bulk", body, { headers: { "Content-Type": "multipart/form-data" } })).data;
  };

  const check = async (chosen, update) => {
    setBusy("checking"); setPlan(null); setOnlyProblems(false);
    try {
      setPlan(await send(chosen, true, update));
    } catch (err) {
      explain(err, "The file could not be read");
      setFile(null);
    } finally { setBusy(""); }
  };

  const pick = (e) => {
    const chosen = e.target.files?.[0];
    e.target.value = "";                            // the same file can be chosen again after it is corrected
    if (!chosen) return;
    if (chosen.size > MAX_BYTES) { toast.error("The file is larger than 2 MB. Upload up to 500 services at a time."); return; }
    setFile(chosen);
    check(chosen, updateExisting);
  };

  const toggleUpdate = (value) => {
    setUpdateExisting(value);
    if (file && plan?.dry_run) check(file, value);
  };

  const run = async () => {
    if (busy || importing.current) return;
    importing.current = true;
    setBusy("importing");
    try {
      const done = await send(file, false, updateExisting);
      setPlan(done);
      const { created, updated } = done.imported;
      toast.success(`${created} service${created === 1 ? "" : "s"} added${updated ? `, ${updated} updated` : ""}`);
      if (created || updated) onImported();
    } catch (err) {
      explain(err, "The services could not be imported");
    } finally { importing.current = false; setBusy(""); }
  };

  const download = async (kind) => {
    setBusy(kind);
    try {
      const r = await api.get(`/tenant/services/bulk/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = kind === "template" ? "services-template.csv" : "my-services.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      explain(err, "The file could not be downloaded");
    } finally { setBusy(""); }
  };

  const startOver = () => { setFile(null); setPlan(null); };

  const summary = plan?.summary;
  const toDo = summary ? summary.create + summary.update : 0;
  const rows = plan ? plan.rows.filter((r) => !onlyProblems || r.action === "error") : [];
  const done = plan && !plan.dry_run;

  return (
    <section className="bg-white border border-neutral-200 p-4 sm:p-6 mb-6 min-w-0" data-testid="bulk-upload">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="admin-overline">Bulk upload</div>
          <p className="text-sm text-neutral-700 mt-1 max-w-2xl">
            Add or change many services at once from a spreadsheet: one service per row. Nothing is saved until you have seen what the file will do and pressed Import.
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close bulk upload" className="icon-btn hover:bg-neutral-100 flex-shrink-0" data-testid="bulk-upload-close"><X className="h-4 w-4" /></button>
      </div>

      <ol className="mt-4 space-y-4 text-sm">
        <li>
          <div className="font-semibold">1. Get the layout</div>
          <p className="text-xs text-neutral-500 mt-0.5">The template opens in Excel or Google Sheets. “My services” is the same layout filled with what you have now: edit it and upload it back to change many at once.</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <button type="button" onClick={() => download("template")} disabled={!!busy} className={`${button} border border-black`} data-testid="bulk-upload-template">
              <Download className="h-4 w-4" /> {busy === "template" ? "Downloading…" : "Download template"}
            </button>
            <button type="button" onClick={() => download("export")} disabled={!!busy} className={`${button} border border-neutral-300`} data-testid="bulk-upload-export">
              <Download className="h-4 w-4" /> {busy === "export" ? "Downloading…" : "Download my services"}
            </button>
          </div>
        </li>
        <li>
          <div className="font-semibold">2. Choose your file</div>
          <p className="text-xs text-neutral-500 mt-0.5">CSV or Excel (.xlsx), up to 500 services. Name, Category and Price are required; a category you do not have yet is created.</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
            <button type="button" onClick={() => input.current?.click()} disabled={!!busy} className={`${button} bg-black text-white`} data-testid="bulk-upload-pick">
              <Upload className="h-4 w-4" /> {file ? "Choose another file" : "Choose file"}
            </button>
            <input ref={input} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={pick} className="hidden" data-testid="bulk-upload-input" />
            <label className="text-xs flex items-center gap-2 min-h-[44px]">
              <input type="checkbox" checked={updateExisting} onChange={(e) => toggleUpdate(e.target.checked)} disabled={!!busy || done} data-testid="bulk-upload-update-existing" />
              Update services that already exist (matched by name)
            </label>
          </div>
          {file && <div className="text-xs text-neutral-600 mt-2 flex items-center gap-1.5 break-anywhere" data-testid="bulk-upload-file"><FileSpreadsheet className="h-4 w-4 flex-shrink-0" /> {file.name}</div>}
        </li>
      </ol>

      {busy === "checking" && <div className="text-sm text-neutral-500 mt-4" data-testid="bulk-upload-checking">Checking the file…</div>}

      {plan && (
        <div className="mt-5 border-t border-neutral-200 pt-4" data-testid="bulk-upload-plan">
          <div className="font-semibold text-sm">{done ? "Done" : "3. Check what the file will do"}</div>
          {done && (
            <p className="text-sm text-neutral-700 mt-1" data-testid="bulk-upload-result">
              {plan.imported.created} added, {plan.imported.updated} updated{plan.imported.categories ? `, ${plan.imported.categories} new categor${plan.imported.categories === 1 ? "y" : "ies"}` : ""}.
              {summary.error ? ` ${summary.error} row${summary.error === 1 ? " was" : "s were"} left out: correct ${summary.error === 1 ? "it" : "them"} in the file and upload it again (what is already in is skipped).` : ""}
            </p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid="bulk-upload-summary">
            {[["create", done ? "Added" : "New"], ["update", done ? "Updated" : "Updates"], ["skip", "Skipped"], ["error", "Problems"]].map(([key, label]) => (
              <div key={key} className={`border p-3 ${key === "error" && summary.error ? "border-red-300 bg-red-50" : "border-neutral-200"}`} data-testid={`bulk-upload-count-${key}`}>
                <div className="admin-overline">{label}</div>
                <div className="admin-heading text-2xl font-bold mt-0.5">{summary[key]}</div>
              </div>
            ))}
          </div>
          {plan.new_categories.length > 0 && (
            <p className="text-xs text-neutral-700 mt-3 break-words" data-testid="bulk-upload-new-categories">
              {done ? "New categories created" : "New categories that will be created"}: <span className="font-semibold">{plan.new_categories.join(", ")}</span>. {!done && "If one of these is a spelling mistake, correct it in the file first."}
            </p>
          )}
          {plan.warnings.map((w) => <p key={w} className="text-xs text-neutral-500 mt-1 break-words">{w}</p>)}

          <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
            <div className="text-xs text-neutral-500">{plan.total} row{plan.total === 1 ? "" : "s"} read{rows.length > SHOWN ? `, first ${SHOWN} shown` : ""}</div>
            {summary.error > 0 && (
              <label className="text-xs flex items-center gap-2 min-h-[44px]">
                <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} data-testid="bulk-upload-only-problems" /> Show only rows with problems
              </label>
            )}
          </div>
          <ul className="mt-1 divide-y divide-neutral-100 border border-neutral-200 max-h-[26rem] overflow-y-auto" data-testid="bulk-upload-rows">
            {rows.slice(0, SHOWN).map((r) => (
              <li key={r.line} className="p-3 flex items-start gap-3" data-testid={`bulk-upload-row-${r.line}`}>
                <span className={`flex-shrink-0 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide ${ACTION[r.action].pill}`}>{ACTION[r.action].label}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold break-words">{r.name || <span className="text-neutral-400">(no name)</span>}</div>
                  <div className="text-xs text-neutral-500 break-words">
                    Row {r.line}{r.category ? ` · ${r.category}` : ""}{r.price != null ? ` · ${formatINR(r.price)}` : ""}{r.discount_price ? ` (${formatINR(r.discount_price)} after discount)` : ""}
                    {r.changes.length > 0 && ` · changes: ${r.changes.join(", ")}`}
                  </div>
                  {r.messages.map((m) => <div key={m} className={`text-xs mt-1 break-words ${r.action === "error" ? "text-red-700" : "text-neutral-600"}`}>{m}</div>)}
                </div>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-3 mt-4">
            {!done && (
              <button type="button" onClick={run} disabled={!!busy || toDo === 0} className={`${button} bg-black text-white`} data-testid="bulk-upload-import">
                {busy === "importing" ? "Importing…" : `Import ${toDo} service${toDo === 1 ? "" : "s"}`}
              </button>
            )}
            <button type="button" onClick={done ? startOver : onClose} disabled={busy === "importing"} className={`${button} border border-neutral-300`} data-testid="bulk-upload-reset">
              {done ? "Upload another file" : "Cancel"}
            </button>
            {!done && summary.error > 0 && toDo > 0 && <span className="text-xs text-red-700">{summary.error} row{summary.error === 1 ? "" : "s"} with problems will be left out.</span>}
            {!done && toDo === 0 && <span className="text-xs text-neutral-600" data-testid="bulk-upload-nothing">Nothing in this file would be added or changed.</span>}
          </div>
        </div>
      )}
    </section>
  );
}
